"use client";

import { useInfront } from "@/context/infront-context";
import {
  createInstrumentSlug,
  DEFAULT_CONTENT,
} from "@/services/infront/utils";
import { useRouter } from "next/navigation";
import { useEffect, useRef } from "react";

const ID = "market-list";

const INDICIES = [
  { feed: 17921, ticker: "OMXS30" },
  { feed: 17921, ticker: "OMXSPI" },
  { feed: 2087, ticker: "COMP" },
  { feed: 18177, ticker: "OBX" },
  { feed: 17665, ticker: "OMXC25" },
  { feed: 100, ticker: "OMXH25" },
  { feed: 2302, ticker: "BDE40P" },
  { feed: 6150, ticker: "N225" },
];

const CURRENCIES = [
  { feed: 12, ticker: "EURSEK" },
  { feed: 12, ticker: "USDSEK" },
  { feed: 12, ticker: "GBPSEK" },
  { feed: 12, ticker: "DKKSEK" },
  { feed: 12, ticker: "NOKSEK" },
  { feed: 12, ticker: "EURGBP" },
  { feed: 12, ticker: "GBPUSD" },
];

const MarketList = () => {
  const router = useRouter();
  const { isReady, load } = useInfront();

  const widgetRef = useRef<any>(null);
  const observerRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    let active = true;

    load?.(["core", "highstock", "hollowcandlestick", "heikinashi"]).then(() => {
      if (!isReady || !active) return;

      widgetRef.current?.destroy();
      widgetRef.current = null;

      const widgetOptions = new window.Infront.QuoteListWidgetOptions();

      widgetOptions.onInstrumentSelected = (instrument: {
        feed: number;
        ticker: string;
      }) => {
        router.push(createInstrumentSlug(instrument.feed, instrument.ticker));
      };

      widgetOptions.widgetTitle = "Marknad";
      widgetOptions.defaultContent = DEFAULT_CONTENT;
      widgetOptions.enableChangeStatusColors = true;
      widgetOptions.instruments = INDICIES;

      widgetOptions.columns = [
        {
          name: "TICKER",
          flag: true,
        },
        "LAST",
        "PCT_CHANGE",
        "TIME",
      ];

      const dropdownOptions = new window.Infront.DropDownOptions();

      dropdownOptions.title = "Instrument";
      dropdownOptions.list = ["Index", "Valutor"];
      dropdownOptions.closeOnClick = true;
      dropdownOptions.sortAlphabetically = false;
      dropdownOptions.preSelectedItems = dropdownOptions.list[0];
      dropdownOptions.onItemClicked = (item: string) => {
        dropdownOptions.preSelectedItems = item;

        switch (item) {
          case "Index":
            widgetOptions.instruments = INDICIES;
            break;
          case "Valutor":
            widgetOptions.instruments = CURRENCIES;
            break;
        }

        reInitList();
      };

      widgetOptions.customDropDowns = [dropdownOptions];

      widgetRef.current = window.infront.quoteList(`#${ID}`, widgetOptions);

      const reInitList = () => {
        widgetRef.current?.destroy();
        widgetRef.current = window.infront.quoteList(`#${ID}`, widgetOptions);
      };

      const container = document.getElementById(ID);
      if (container) {
        const applyLabels = () => {
          container.querySelectorAll('span[aria-label="TICKER"]').forEach((el) => {
            if (el.textContent?.trim() === "COMP") el.textContent = "NDX";
          });
        };
        applyLabels();
        observerRef.current?.disconnect();
        observerRef.current = new MutationObserver(applyLabels);
        observerRef.current.observe(container, { childList: true, subtree: true });
      }
    });

    return () => {
      active = false;
      widgetRef.current?.destroy();
      widgetRef.current = null;
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [isReady, router]);

  return <div className="card" id={ID} />;
};

export default MarketList;
